import { useState, useRef, useMemo } from "react";
import {
  Plus, X, Trash2, Check, Play, Undo2, Mail, Phone, Star, Copy, Zap, Bell, BellOff, Lock, LockOpen
} from "lucide-react";
import { motion } from "framer-motion";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

// Organic Highlighter Component - creates a natural, hand-drawn highlighter effect
export const HighlighterText = ({ children, isActive, isDone }) => {
  if (!isActive) {
    return <span>{children}</span>;
  }

  return (
    <span
      className="relative inline"
      style={{
        background: isDone
          ? 'linear-gradient(to bottom, transparent 5%, rgba(254, 243, 199, 0.25) 12%, rgba(254, 243, 199, 0.28) 88%, transparent 95%)'
          : 'linear-gradient(to bottom, transparent 3%, rgba(253, 224, 71, 0.28) 10%, rgba(253, 224, 71, 0.38) 50%, rgba(253, 224, 71, 0.32) 90%, transparent 97%)',
        padding: '0.12em 0.3em',
        margin: '0 -0.3em',
        borderRadius: '3px',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      }}
    >
      <span
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5 Q 25 3, 50 6 T 100 4 T 150 7 T 200 5' stroke='%23fbbf24' stroke-width='1' fill='none' opacity='0.08'/%3E%3Cpath d='M0 95 Q 25 97, 50 94 T 100 96 T 150 93 T 200 95' stroke='%23fbbf24' stroke-width='1' fill='none' opacity='0.08'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100%',
          backgroundRepeat: 'repeat-x',
          mixBlendMode: 'multiply',
          opacity: 0.6,
        }}
      />
      <span className="relative">{children}</span>
    </span>
  );
};

// Category icon component - consistent line icons for task types
export const CategoryIconDisplay = ({ type, size = 14, className = "" }) => {
  const iconProps = {
    size,
    strokeWidth: 1.5,
    className: `text-stone-400 ${className}`
  };

  switch (type) {
    case 'action':
      return <Zap {...iconProps} />;
    case 'email':
      return <Mail {...iconProps} />;
    case 'phone':
      return <Phone {...iconProps} />;
    default:
      return null;
  }
};

// Category icon tag for cards - shows subtle icon for email/phone tasks
export const CategoryTag = ({ category, size = 'sm' }) => {
  if (!category || category === 'action') return null;
  const iconSize = size === 'sm' ? 11 : 13;
  return (
    <span className="opacity-50" title={category === 'email' ? 'Email' : 'Phone Call'}>
      <CategoryIconDisplay type={category} size={iconSize} className="inline-block" />
    </span>
  );
};

// Phone Contact Chip - uses tel: link on mobile, copy-to-clipboard on desktop
export const PhoneContactChip = ({ contact, cardId }) => {
  const [copied, setCopied] = useState(false);

  const isMobile = typeof window !== 'undefined' && (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(contact.phone);
      setCopied(true);
      toast.success('Phone number copied!', { duration: 2000 });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  if (isMobile) {
    return (
      <a
        href={`tel:${contact.phone}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 text-xs font-medium transition-colors"
        data-testid={`contact-chip-phone-${cardId}`}
      >
        <Phone size={12} />
        <span>{contact.name || contact.phone}</span>
      </a>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 text-green-600 text-xs font-medium"
      onClick={(e) => e.stopPropagation()}
      data-testid={`contact-chip-phone-${cardId}`}
    >
      <Phone size={12} />
      <span>{contact.name ? `${contact.name} · ${contact.phone}` : contact.phone}</span>
      <button
        onClick={handleCopy}
        className="ml-1 p-0.5 rounded hover:bg-green-100 transition-colors"
        title="Copy phone number"
        data-testid={`copy-phone-${cardId}`}
      >
        {copied ? (
          <Check size={11} className="text-green-500" />
        ) : (
          <Copy size={11} className="text-green-400 hover:text-green-600" />
        )}
      </button>
    </div>
  );
};

// Category selector buttons for edit modal / add form
export const CategorySelector = ({ value, onChange }) => {
  const categories = [
    { key: 'action', label: 'Action', icon: Zap },
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'phone', label: 'Call', icon: Phone },
  ];
  return (
    <div className="flex gap-2">
      {categories.map(cat => (
        <button
          key={cat.key}
          type="button"
          onClick={() => onChange(cat.key)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
            value === cat.key
              ? 'bg-[#F58227]/10 text-[#F58227] border border-[#F58227]/30'
              : 'bg-[#FDFBF7] text-stone-500 border border-stone-200 hover:border-stone-300'
          }`}
          data-testid={`category-btn-${cat.key}`}
        >
          <cat.icon size={12} strokeWidth={1.5} className={value === cat.key ? 'text-[#F58227]' : 'text-stone-400'} />
          {cat.label}
        </button>
      ))}
    </div>
  );
};

// Thread age border color - subtle left border that shifts over time
export const getThreadAgeBorderColor = (createdAt) => {
  if (!createdAt) return 'transparent';
  const daysOpen = Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
  if (daysOpen <= 1) return '#3DC4D0';
  if (daysOpen <= 3) return '#6DD9D0';
  if (daysOpen <= 6) return '#A8D5C8';
  if (daysOpen <= 13) return '#D4C4B0';
  return '#E8E4E0';
};

// Format completion date for Done cards
export const formatCompletionDate = (completedAt) => {
  if (!completedAt) return null;

  const now = new Date();
  const completed = new Date(completedAt);
  const diffMs = now - completed;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 6) return `${diffDays}d ago`;

  const options = { month: 'short', day: 'numeric' };
  return completed.toLocaleDateString('en-US', options);
};

// Critical Toggle Button - Asterisk indicator
export const CriticalToggle = ({ isCritical, onToggle, cardId }) => {
  return (
    <motion.button
      onClick={(e) => {
        e.stopPropagation();
        onToggle(cardId);
      }}
      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all flex-shrink-0 ${
        isCritical
          ? 'text-amber-400/80'
          : 'text-stone-400/50 hover:text-amber-300'
      }`}
      whileTap={{ scale: 0.85 }}
      title={isCritical ? "Clear importance" : "Mark as important"}
      aria-label={isCritical ? "Remove important marker" : "Mark task as important"}
      data-testid={`critical-toggle-${cardId}`}
    >
      <motion.span
        initial={false}
        animate={isCritical ? { scale: [1, 1.25, 1], rotate: [0, 15, 0] } : { scale: 1 }}
        transition={{ duration: 0.3 }}
        className="text-[26px] font-bold leading-none select-none"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        *
      </motion.span>
    </motion.button>
  );
};

// Custom Grip Handle with adjustable dot spacing
export const GripDots = ({ className }) => (
  <div className={`grid grid-cols-2 gap-[2.6px] ${className}`}>
    <div className="w-[3.15px] h-[3.15px] rounded-full bg-current" />
    <div className="w-[3.15px] h-[3.15px] rounded-full bg-current" />
    <div className="w-[3.15px] h-[3.15px] rounded-full bg-current" />
    <div className="w-[3.15px] h-[3.15px] rounded-full bg-current" />
    <div className="w-[3.15px] h-[3.15px] rounded-full bg-current" />
    <div className="w-[3.15px] h-[3.15px] rounded-full bg-current" />
  </div>
);

export const SortableCard = ({ card, listId, listName, onEdit, onDelete, onMarkDone, onMoveToList, onToggleCritical, onToggleNudge, onShare, isHighlighted, isFirstCard, showEditHint, onDismissEditHint, nudgeSupported, onLockScreenPin, lockScreenTaskId }) => {
  const cardRef = useRef(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id, data: { type: "card", card, listId } });

  const combinedRef = (node) => {
    setNodeRef(node);
    cardRef.current = node;
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const isDoneList = listName?.toLowerCase() === "done";
  const isInProgress = listName?.toLowerCase() === "doing" || listName?.toLowerCase() === "in progress";
  const isToDoList = listName?.toLowerCase() === "to do";
  const isCritical = card.isCritical || false;
  const isLockScreenFocus = lockScreenTaskId && card.id === lockScreenTaskId;

  const getCriticalBgClass = () => {
    if (isDoneList) return 'bg-[var(--card-done)]';
    return 'bg-[var(--card-white)]';
  };

  const getCriticalBorderClass = () => {
    if (isLockScreenFocus) return 'animate-focus-halo';
    if (!isCritical) return isHighlighted ? 'ring-4 ring-[#3DC4D0]/20' : '';
    return '';
  };

  const getShadowClass = () => {
    if (isDragging) return '';
    if (isHighlighted) return '';
    return '';
  };

  const cardShadow = isDragging
    ? { boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)', border: '1px solid var(--card-border)' }
    : isHighlighted
      ? { boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)', border: '1px solid var(--card-border)' }
      : isDoneList
        ? { boxShadow: 'var(--elevation-1-done)', border: '1px solid var(--card-border)' }
        : { boxShadow: 'var(--elevation-1)', border: '1px solid var(--card-border)' };

  const threadAgeBorder = isInProgress ? {
    borderLeftWidth: '3px',
    borderLeftColor: getThreadAgeBorderColor(card.createdAt),
  } : {};

  return (
    <motion.div
      ref={combinedRef}
      style={{ ...style, ...cardShadow, ...threadAgeBorder, transition: 'border-color 0.3s ease-out, box-shadow 0.2s ease-out, transform 0.2s ease-out' }}
      className={`${getCriticalBgClass()} rounded-xl px-3 py-2 mb-2 md:mb-[13px] group ${getShadowClass()} ${getCriticalBorderClass()} relative overflow-hidden ${isHighlighted ? 'z-40' : ''}`}
      data-testid={`card-${card.id}`}
      initial={isDoneList ? { opacity: 0, y: -6, scale: 1.005 } : false}
      animate={
        isHighlighted
          ? { scale: 1.01, y: -2, opacity: isDoneList ? 0.88 : 1 }
          : isDoneList
            ? { scale: 1, y: [null, 1.5, 0], opacity: 0.88 }
            : { scale: 1, y: 0, opacity: 1 }
      }
      transition={
        isHighlighted
          ? { duration: 0.3, ease: 'easeOut' }
          : isDoneList
            ? { duration: 0.4, ease: [0.25, 0.1, 0.25, 1], y: { duration: 0.35 } }
            : { duration: 0.4, ease: 'easeOut' }
      }
    >
      {!isDoneList && (
        <div
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.3) 20%, transparent 50%)',
          }}
        />
      )}
      {!isDoneList && (
        <div
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.01) 15%, transparent 35%)',
          }}
        />
      )}

      <div className="flex items-center relative z-10 mb-1">
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <CriticalToggle
            isCritical={isCritical}
            onToggle={onToggleCritical}
            cardId={card.id}
          />

          {nudgeSupported && !isDoneList && (
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleNudge?.(card.id, card.content, !card.nudgeEnabled);
              }}
              className={`p-1.5 rounded-lg transition-all ${
                card.nudgeEnabled
                  ? 'text-[#00838F] hover:bg-[#E0F7FA]'
                  : 'text-stone-400 hover:text-stone-500 hover:bg-stone-100'
              }`}
              title={card.nudgeEnabled ? "Email nudge enabled (tap to disable)" : "Enable email nudge (24 hrs)"}
              data-testid={`nudge-toggle-${card.id}`}
              whileTap={{ scale: 0.9 }}
            >
              {card.nudgeEnabled ? <Bell size={14} /> : <BellOff size={14} />}
            </motion.button>
          )}

          {(isInProgress || isToDoList) && onLockScreenPin && (
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onLockScreenPin(card.id, card.content);
              }}
              className={`p-1.5 rounded-lg transition-all ${
                isLockScreenFocus
                  ? 'text-[#0A9699] hover:bg-[#E0F7FA]'
                  : 'text-stone-400 hover:text-stone-500 hover:bg-stone-100'
              }`}
              title={isLockScreenFocus ? "This is your Lock Screen focus (tap to change)" : "Keep this on my Lock Screen"}
              data-testid={`lockscreen-toggle-${card.id}`}
              whileTap={{ scale: 0.9 }}
            >
              {isLockScreenFocus ? <Lock size={14} /> : <LockOpen size={14} />}
            </motion.button>
          )}

          <button
            {...attributes}
            {...listeners}
            className="w-9 h-10 flex items-center justify-center text-[#A39E97]/70 hover:text-stone-500 cursor-grab active:cursor-grabbing touch-none"
          >
            <GripDots />
          </button>

          {isToDoList && (
            <motion.button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "Doing");
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "Doing");
              }}
              className="p-2 text-[#847D76]/70 hover:text-[#3DC4D0] hover:bg-[#E0F7FA] active:bg-[#E0F7FA] rounded-lg transition-all touch-manipulation"
              title="Push to Doing"
              data-testid={`move-progress-${card.id}`}
              whileTap={{ scale: 0.9, boxShadow: '0 0 8px rgba(61, 196, 208, 0.3)' }}
            >
              <Play size={16} />
            </motion.button>
          )}

          {isInProgress && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "To Do");
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "To Do");
              }}
              className="p-2 text-[#847D76]/70 hover:text-[#F58227] hover:bg-orange-50 active:bg-orange-50 rounded-lg transition-colors touch-manipulation"
              title="Return to To Do"
              data-testid={`return-todo-${card.id}`}
            >
              <Undo2 size={16} />
            </button>
          )}
        </div>

        {!isDoneList && <div className="w-4" />}

        {!isDoneList && (
          <motion.button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMarkDone(card.id, listId, cardRef.current);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMarkDone(card.id, listId, cardRef.current);
            }}
            className="p-2.5 text-green-500/65 hover:text-green-500/90 hover:bg-green-50 active:bg-green-50 rounded-xl transition-colors touch-manipulation flex-shrink-0"
            title="Mark as done"
            data-testid={`mark-done-${card.id}`}
            whileTap={{ scale: 0.85, backgroundColor: 'rgba(34, 197, 94, 0.15)' }}
            transition={{ duration: 0.1 }}
          >
            <Check size={21} strokeWidth={2.5} />
          </motion.button>
        )}

        {isDoneList && (
          <div className="flex items-center gap-2.5">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "Doing");
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "Doing");
              }}
              className="p-2.5 text-[#969089] hover:text-[#3DC4D0] hover:bg-[#E0F7FA] active:bg-[#E0F7FA] rounded-lg transition-colors touch-manipulation opacity-60"
              title="Push to Doing"
              data-testid={`move-progress-${card.id}`}
            >
              <Play size={15} />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "To Do");
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveToList(card.id, listId, "To Do");
              }}
              className="p-2.5 text-[#969089] hover:text-[#F58227] hover:bg-orange-50 active:bg-orange-50 rounded-lg transition-colors touch-manipulation opacity-60"
              title="Move to To Do"
              data-testid={`move-todo-${card.id}`}
            >
              <Undo2 size={15} />
            </button>
          </div>
        )}
      </div>

      <motion.div
        className="px-1 py-2 cursor-pointer rounded-lg hover:bg-stone-50/50 transition-all -mx-1"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onDismissEditHint) {
            onDismissEditHint();
          }
          onEdit(card);
        }}
        whileTap={{
          scale: 0.97,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.03)'
        }}
        transition={{ duration: 0.1 }}
        title="Tap to edit"
      >
        <p className={`text-[17px] leading-snug break-words font-medium tracking-normal ${isDoneList ? 'text-[#9CA3AF] line-through decoration-[#a1a1a1]' : 'text-[#0F0F11]'}`}>
          <HighlighterText isActive={isCritical && !isDoneList} isDone={isDoneList}>
            {card.content}
          </HighlighterText>
        </p>
        {card.notes && (
          <p className="text-[#A1A1AA] text-sm mt-1.5 break-words">{card.notes}</p>
        )}
        {isFirstCard && showEditHint && !isDoneList && (
          <p className="text-[13px] text-[#A1A1AA]/60 mt-2 select-none">
            TAP text to EDIT
          </p>
        )}
      </motion.div>

      {card.linkedContact && !isDoneList && (
        <div className="px-1 mt-2">
          {card.category === 'email' && card.linkedContact.email ? (
            <a
              href={`mailto:${card.linkedContact.email}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-medium transition-colors"
              data-testid={`contact-chip-email-${card.id}`}
            >
              <Mail size={12} />
              <span>{card.linkedContact.name || card.linkedContact.email}</span>
            </a>
          ) : card.category === 'phone' && card.linkedContact.phone ? (
            <PhoneContactChip
              contact={card.linkedContact}
              cardId={card.id}
            />
          ) : null}
        </div>
      )}

      {!card.linkedContact && (card.category === 'email' || card.category === 'phone') && !isDoneList && (
        <div className="px-1 mt-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit(card);
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-stone-300 bg-stone-50/50 hover:bg-stone-100 text-stone-400 hover:text-stone-500 text-xs font-medium transition-colors"
            data-testid={`add-contact-chip-${card.id}`}
          >
            {card.category === 'email' ? <Mail size={12} /> : <Phone size={12} />}
            <span>Add {card.category === 'email' ? 'email' : 'phone'}</span>
          </button>
        </div>
      )}

      {(isInProgress || isToDoList) && isCritical && (
        <div className="flex items-center gap-1.5 mt-1.5 px-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100/80 text-amber-700 text-[10px] font-medium uppercase tracking-wide">
            <span className="text-[11px] leading-none font-bold" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>*</span>
            Focus
          </span>
        </div>
      )}

      {isDoneList && isCritical && (
        <div className="flex items-center gap-1.5 mt-1.5 px-1 text-xs text-amber-500/80">
          <Star size={10} fill="currentColor" />
          <span>Important · Completed</span>
        </div>
      )}

      {isDoneList && card.completedAt && (
        <div className="flex justify-end mt-1 mr-8">
          <span className="text-[11px] text-[#a3a3a3] opacity-70 group-hover:opacity-90 transition-opacity">
            {formatCompletionDate(card.completedAt)}
          </span>
        </div>
      )}
    </motion.div>
  );
};

// Card Overlay for dragging
export const CardOverlay = ({ card }) => {
  const isCritical = card.isCritical || false;

  return (
    <div
      className={`${isCritical ? 'bg-amber-50' : 'bg-white'} rounded-xl p-4 border-2 border-[#F58227] rotate-3`}
      style={{ boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)' }}
    >
      <div className="flex items-start gap-2">
        {isCritical && (
          <span className="text-[10px] text-amber-400 flex-shrink-0 leading-none font-bold" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>*</span>
        )}
        <p className="text-[#18181B] text-[15px] font-normal">{card.content}</p>
      </div>
    </div>
  );
};

// Droppable List Container - allows dropping cards into empty lists
export const DroppableList = ({ listId, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: listId,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 overflow-y-auto space-y-8 min-h-[100px] rounded-xl transition-colors ${
        isOver ? 'bg-[#E0F7FA]/50' : ''
      }`}
    >
      {children}
    </div>
  );
};

// Column Grip Handle for dragging columns (inline with title)
export const ColumnGripHandle = ({ listeners, attributes }) => {
  return (
    <button
      {...listeners}
      {...attributes}
      className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-500 transition-colors mr-3 flex-shrink-0 p-1"
      title="Drag to reorder column"
      data-column-drag-handle="true"
    >
      <div className="grid grid-cols-2 gap-[3px]">
        <div className="w-1 h-1 rounded-full bg-current" />
        <div className="w-1 h-1 rounded-full bg-current" />
        <div className="w-1 h-1 rounded-full bg-current" />
        <div className="w-1 h-1 rounded-full bg-current" />
        <div className="w-1 h-1 rounded-full bg-current" />
        <div className="w-1 h-1 rounded-full bg-current" />
      </div>
    </button>
  );
};

// Sortable Column Wrapper
export const SortableColumn = ({ list, children, isDragging, columnIndex }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: list.id,
    data: { type: 'column', list }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col snap-center" data-column-index={columnIndex}>
      {children({ listeners, attributes })}
    </div>
  );
};

// List Component
export const ListColumn = ({ list, onAddCard, onEditCard, onDeleteCard, onMarkDone, onMoveToList, onToggleCritical, onToggleNudge, onShareTask, onRenameList, onDeleteList, highlightedCardId, headerContent, footerContent, dragListeners, dragAttributes, searchQuery = "", dailyFocus = false, showEditHint, onDismissEditHint, nudgeSupported, onLockScreenPin, lockScreenTaskId }) => {
  const dismissEditHint = onDismissEditHint;
  const [isAdding, setIsAdding] = useState(false);
  const [newCardText, setNewCardText] = useState("");
  const [newCardImportant, setNewCardImportant] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [listName, setListName] = useState(list.name);
  const isDoneList = list.name.toLowerCase() === "done";
  const isDoingList = list.name.toLowerCase() === "doing" || list.name.toLowerCase() === "in progress";
  const isToDoList = list.name.toLowerCase() === "to do";
  const isGroupableList = isDoingList || isToDoList;

  // Apply Daily Focus filter first (Important-only in To Do/Done, everything in Doing),
  // then the search filter.
  const focusFilteredCards = dailyFocus && !isDoingList
    ? list.cards.filter(card => card.isCritical)
    : list.cards;

  const filteredCards = searchQuery.trim()
    ? focusFilteredCards.filter(card =>
        card.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (card.notes && card.notes.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : focusFilteredCards;

  const groupedCards = useMemo(() => {
    if (!isDoneList) return null;
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    filteredCards.forEach(card => {
      let label;
      if (card.completedAt) {
        const d = new Date(card.completedAt);
        d.setHours(0, 0, 0, 0);
        if (d.getTime() === today.getTime()) label = 'Today';
        else if (d.getTime() === yesterday.getTime()) label = 'Yesterday';
        else label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } else {
        label = 'Earlier';
      }
      if (!groups[label]) groups[label] = [];
      groups[label].push(card);
    });

    const order = ['Today', 'Yesterday'];
    return Object.entries(groups).sort(([a], [b]) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      if (a === 'Earlier') return 1;
      if (b === 'Earlier') return -1;
      return 0;
    });
  }, [isDoneList, filteredCards]);

  const categoryGrouped = useMemo(() => {
    if (!isGroupableList) return null;
    const hasEmail = filteredCards.some(c => c.category === 'email');
    const hasPhone = filteredCards.some(c => c.category === 'phone');
    if (!hasEmail && !hasPhone) return null;

    const sections = [];
    const actionCards = filteredCards.filter(c => !c.category || c.category === 'action');
    const emailCards = filteredCards.filter(c => c.category === 'email');
    const phoneCards = filteredCards.filter(c => c.category === 'phone');

    if (actionCards.length > 0) sections.push({ key: 'action', label: 'Action', icon: 'action', cards: actionCards });
    if (emailCards.length > 0) sections.push({ key: 'email', label: 'Emails', icon: 'email', cards: emailCards });
    if (phoneCards.length > 0) sections.push({ key: 'phone', label: 'Calls', icon: 'phone', cards: phoneCards });

    return sections;
  }, [isGroupableList, filteredCards]);

  const sortedFilteredCards = useMemo(() => {
    if (!categoryGrouped) return filteredCards;
    return categoryGrouped.flatMap(s => s.cards);
  }, [categoryGrouped, filteredCards]);

  const handleAddCard = () => {
    if (newCardText.trim()) {
      onAddCard(list.id, newCardText.trim(), newCardImportant);
      setNewCardText("");
      setNewCardImportant(false);
      setIsAdding(false);
      document.activeElement?.blur();
    }
  };

  const handleRename = () => {
    if (listName.trim() && listName !== list.name) {
      onRenameList(list.id, listName.trim());
    }
    setIsEditingName(false);
  };

  const isProtectedList = list.name.toLowerCase() === "to do" || list.name.toLowerCase() === "done" || list.name.toLowerCase() === "doing" || list.name.toLowerCase() === "in progress";

  const getColumnBackground = () => {
    const name = list.name.toLowerCase();
    if (name === 'to do') return 'var(--column-todo)';
    if (name === 'doing' || name === 'in progress') return 'var(--column-doing)';
    if (name === 'done') return 'var(--column-done)';
    return 'var(--column-todo)';
  };

  return (
    <div className="flex flex-col snap-center">
      {headerContent}

      <div
        className="rounded-2xl p-4 w-72 flex-shrink-0 flex flex-col max-h-[calc(100vh-200px)] relative overflow-hidden"
        style={{
          backgroundColor: getColumnBackground(),
          boxShadow: 'var(--elevation-1)',
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          style={{ background: 'var(--card-surface-light)' }}
        />
        <div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          style={{ background: 'var(--card-surface-dark)' }}
        />

        <div className="flex items-center justify-between mb-4 relative z-10">
          <div className="flex items-center">
            {dragListeners && dragAttributes && (
              <button
                {...dragListeners}
                {...dragAttributes}
                className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-500 transition-colors p-2 -ml-2 mr-3 touch-none"
                title="Drag to reorder column"
                data-column-drag-handle="true"
              >
                <div className="grid grid-cols-2 gap-[2px]">
                  <div className="w-[3px] h-[3px] rounded-full bg-current" />
                  <div className="w-[3px] h-[3px] rounded-full bg-current" />
                  <div className="w-[3px] h-[3px] rounded-full bg-current" />
                  <div className="w-[3px] h-[3px] rounded-full bg-current" />
                  <div className="w-[3px] h-[3px] rounded-full bg-current" />
                  <div className="w-[3px] h-[3px] rounded-full bg-current" />
                </div>
              </button>
            )}
            {isEditingName ? (
              <input
                type="text"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                onBlur={handleRename}
                onKeyDown={(e) => e.key === "Enter" && handleRename()}
                className="flex-1 bg-white rounded-lg px-3 py-1 text-sm font-bold text-[#1A1A1C] border border-[#F58227] outline-none"
                autoFocus
              />
            ) : (
              <h3
                onClick={() => setIsEditingName(true)}
                className={`font-bold text-lg cursor-pointer transition-colors uppercase ${
                  isDoneList
                    ? 'text-[#4CAF50] hover:text-[#43A047]'
                    : 'text-[#1A1A1C] hover:text-[#F58227]'
                }`}
                style={{ letterSpacing: '0.05em' }}
              >
                {isToDoList && list.name.toLowerCase() === 'to do' ? (
                  <>
                    TO <span className="text-[#4CAF50]">DO</span>
                  </>
                ) : (
                  list.name
                )}
              </h3>
            )}
          </div>

          {!isProtectedList && (
            <button
              onClick={() => onDeleteList(list.id)}
              className="p-1 text-stone-400 hover:text-red-500 transition-colors"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>

        <DroppableList listId={list.id}>
          <SortableContext items={(categoryGrouped ? sortedFilteredCards : filteredCards).map(c => c.id)} strategy={verticalListSortingStrategy}>
            {filteredCards.length === 0 && searchQuery.trim() ? (
              <p
                className="text-sm text-stone-400 italic py-4 text-center cursor-pointer hover:text-stone-500 font-light"
                onClick={() => {}}
                data-dismiss-search="true"
              >
                No matching tasks
              </p>
            ) : filteredCards.length === 0 && dailyFocus && !isDoingList ? (
              <p className="text-xs text-stone-400 italic py-4 text-center font-light">
                {isToDoList ? "No important tasks queued" : "No important tasks completed"}
              </p>
            ) : isDoneList && groupedCards ? (
              groupedCards.map(([label, cards], gi) => (
                <div key={label}>
                  {gi > 0 && <div className="border-t border-stone-200/50 my-2" />}
                  <p className="text-[11px] font-light text-[#A1A1AA] uppercase tracking-widest mb-3 mt-1 px-1">{label}</p>
                  <div>
                    {cards.map((card, index) => (
                      <SortableCard
                        key={card.id}
                        card={card}
                        listId={list.id}
                        listName={list.name}
                        onEdit={onEditCard}
                        onDelete={onDeleteCard}
                        onMarkDone={onMarkDone}
                        onMoveToList={onMoveToList}
                        onToggleCritical={onToggleCritical}
                        onToggleNudge={onToggleNudge}
                        onShare={onShareTask}
                        isHighlighted={highlightedCardId === card.id || (searchQuery.trim() && card.content.toLowerCase().includes(searchQuery.toLowerCase()))}
                        isFirstCard={gi === 0 && index === 0}
                        showEditHint={showEditHint}
                        onDismissEditHint={dismissEditHint}
                        nudgeSupported={nudgeSupported}
                        onLockScreenPin={onLockScreenPin}
                        lockScreenTaskId={lockScreenTaskId}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (isDoingList || isToDoList) && categoryGrouped ? (
              categoryGrouped.map((section, si) => (
                <div key={section.key} data-testid={`doing-section-${section.key}`}>
                  {si > 0 && <div className="border-t border-stone-200/30 my-2" />}
                  <p className="text-[11px] font-medium text-[#8B8B8B] uppercase tracking-wider mb-3 mt-1 px-1 flex items-center gap-1.5">
                    <CategoryIconDisplay type={section.icon} size={10} className="text-stone-400" />
                    {section.label}
                  </p>
                  <div>
                    {section.cards.map((card, index) => (
                      <SortableCard
                        key={card.id}
                        card={card}
                        listId={list.id}
                        listName={list.name}
                        onEdit={onEditCard}
                        onDelete={onDeleteCard}
                        onMarkDone={onMarkDone}
                        onMoveToList={onMoveToList}
                        onToggleCritical={onToggleCritical}
                        onToggleNudge={onToggleNudge}
                        onShare={onShareTask}
                        isHighlighted={highlightedCardId === card.id || (searchQuery.trim() && card.content.toLowerCase().includes(searchQuery.toLowerCase()))}
                        isFirstCard={si === 0 && index === 0}
                        showEditHint={showEditHint}
                        onDismissEditHint={dismissEditHint}
                        nudgeSupported={nudgeSupported}
                        onLockScreenPin={onLockScreenPin}
                        lockScreenTaskId={lockScreenTaskId}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div>
                {filteredCards.map((card, index) => (
                  <SortableCard
                    key={card.id}
                    card={card}
                    listId={list.id}
                    listName={list.name}
                    onEdit={onEditCard}
                    onDelete={onDeleteCard}
                    onMarkDone={onMarkDone}
                    onMoveToList={onMoveToList}
                    onToggleCritical={onToggleCritical}
                    onToggleNudge={onToggleNudge}
                    onShare={onShareTask}
                    isHighlighted={highlightedCardId === card.id || (searchQuery.trim() && card.content.toLowerCase().includes(searchQuery.toLowerCase()))}
                    isFirstCard={index === 0}
                    showEditHint={showEditHint}
                    onDismissEditHint={dismissEditHint}
                    nudgeSupported={nudgeSupported}
                    onLockScreenPin={onLockScreenPin}
                    lockScreenTaskId={lockScreenTaskId}
                  />
                ))}
              </div>
            )}
          </SortableContext>
        </DroppableList>

        {isAdding ? (
          <div className="mt-3 space-y-2">
            <div className="relative rounded-xl">
              <textarea
                value={newCardText}
                onChange={(e) => setNewCardText(e.target.value)}
                placeholder="Add task details here."
                className="w-full rounded-xl p-3 text-base border outline-none resize-none transition-all bg-white border-stone-200 focus:border-[#F58227]"
                style={newCardImportant && newCardText ? { color: 'transparent', caretColor: '#27272A' } : {}}
                rows={3}
                autoFocus
              />
              {newCardImportant && newCardText && (
                <div
                  className="absolute inset-0 pointer-events-none p-3 text-base rounded-xl overflow-hidden"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  <span style={{
                    background: 'linear-gradient(to bottom, transparent 5%, rgba(253, 224, 71, 0.35) 15%, rgba(253, 224, 71, 0.45) 50%, rgba(253, 224, 71, 0.35) 85%, transparent 95%)',
                    padding: '0.15em 0.3em',
                    margin: '0 -0.3em',
                    borderRadius: '3px',
                    boxDecorationBreak: 'clone',
                    WebkitBoxDecorationBreak: 'clone',
                    color: '#27272A',
                  }}>
                    {newCardText}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setNewCardImportant(!newCardImportant)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm ${
                newCardImportant
                  ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-[#FDFBF7] border-stone-200 text-stone-500 hover:border-amber-200 hover:bg-amber-50/50'
              }`}
            >
              <span
                className={`p-[3.9px] rounded leading-none ${
                  newCardImportant
                    ? 'bg-[#4CAF50] ring-2 ring-yellow-400'
                    : 'bg-[#4CAF50]/60 ring-2 ring-yellow-400/60'
                }`}
              >
                <span className="text-[6.8px] font-black text-white flex items-center justify-center w-[7.75px] h-[7.75px]">●</span>
              </span>
              <span className="font-medium">
                {newCardImportant ? "Important" : "Mark as important"}
              </span>
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleAddCard}
                className="flex-1 bg-[#F58227] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#D96D19] transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => { setIsAdding(false); setNewCardText(""); setNewCardImportant(false); }}
                className="px-3 text-stone-500 hover:text-stone-700"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsAdding(true)}
            className="mt-3 flex items-center gap-2 text-[#F58227] hover:text-[#D96D19] text-sm py-2 transition-colors"
          >
            <Plus size={18} />
            Add a task
          </button>
        )}
      </div>

      {footerContent}
    </div>
  );
};
